import { checkIsAgent, excludeAgents } from 'util/participant';
import { createP2pPinger } from 'util/ping';
import {
    AudioTrack,
    ConnectionState,
    LocalParticipant,
    LocalTrackPublication,
    LocalVideoTrack,
    Participant,
    RemoteParticipant,
    RemoteTrack,
    RemoteTrackPublication,
    Room,
    RoomConnectOptions,
    RoomEvent,
    RoomOptions,
    Track,
    VideoQuality,
} from 'livekit-client';
import { useCallback, useRef, useState } from 'react';
import { useRaiseHand } from 'features/RaiseHand/hooks/useRaiseHand';
import { useSort } from 'features/Room/hooks/useSort';
import { initialSort } from 'features/Room/hooks/useInitialSort';
import { useIsMounted } from 'ui/hooks/useIsMounted';
import { useParticipants } from 'useCase/participants/useParticipants';
import { useRoomInfo } from 'useCase/room/useRoomInfo';
import { clearAbsentParticipantSnapshots, clearParticipantSnapshot } from 'hooks/useVideoSnapshot';
import { setLocalCameraQuality, setLocalScreenShareQuality } from 'utils/videoQualityHelpers';
import { reactionsStore } from 'state/store';
import { useReactionsEvents } from 'features/Reactions/hooks/useReactionsEvents';
import { useRoomVirtualBackground } from 'features/VirtualBackground/utils/setupVirtualBackgrounds';
import { useModerationEvents } from 'useCase/moderation/useModerationEvents';
import { createLogger } from '@web-chat-core/toolkit';
import { selectParticipants } from 'state/selectors';
import { LARGE_ROOM_PARTICIPANTS_THRESHOLD } from '../../consts';

const logger = createLogger({ prefix: 'WebRTC Optimization' });

export interface RoomState {
    connect: (url: string, token: string, options?: RoomConnectOptions) => Promise<Room | undefined>;
    isConnecting: boolean;
    room?: Room;
    /* all subscribed audio tracks in the room, not including local participant. */
    audioTracks: AudioTrack[];
    error?: Error;
    connectionState: ConnectionState;
}

export function useRoom(roomOptions?: RoomOptions, hasRecorder = false): RoomState {
    const [room] = useState<Room>(new Room(roomOptions));
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState<Error>();
    const [audioTracks, setAudioTracks] = useState<AudioTrack[]>([]);
    const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
    const { playRaiseHandSound, checkIsHandRaisedInMetadata } = useRaiseHand();
    const { setHasRecorder, updateRoomMetadata } = useRoomInfo();
    const { pingP2pOpponent, stopPingP2pOpponent } = createP2pPinger(room, hasRecorder);
    const {
        addRemoteParticipant,
        removeRemoteParticipant,
        setLocalParticipant,
        setRemoteParticipants,
        setRemoteParticipant,
        updateParticipants,
        setLocalParticipantProperty,
        setRemoteParticipantProperty,
        addScreenShareParticipant,
        removeScreenShareParticipant,
    } = useParticipants();
    const {
        setSortedParticipants,
        sortAfterConnect,
        sortAfterDisconnect,
        sortAfterSpeaking,
        sortAfterScreenSharing,
        sortAfterHandRaised,
    } = useSort();
    const isMounted = useIsMounted();
    // Порядок поднятия рук: первый элемент — первым поднял
    const raisedHandOrder = useRef<string[]>([]);
    setHasRecorder(hasRecorder);
    const { setupRoomBackground } = useRoomVirtualBackground();

    // Подписываемся на события реакций
    useReactionsEvents(room);
    // Подписываемся на события модерации
    useModerationEvents(room);

    const connectFn = useCallback(async (url: string, token: string, options?: RoomConnectOptions) => {
        setIsConnecting(true);

        try {
            const syncSnapshotCacheWithRoom = () => {
                if (!room) {
                    return;
                }

                clearAbsentParticipantSnapshots([
                    room.localParticipant.identity,
                    ...excludeAgents<RemoteParticipant>(room.remoteParticipants).keys(),
                ]);
            };

            const onParticipantConnected = (remoteParticipant: RemoteParticipant) => {
                const isAgent = checkIsAgent(remoteParticipant);

                if (!isAgent) {
                    addRemoteParticipant(remoteParticipant);
                    sortAfterConnect(remoteParticipant);
                    pingP2pOpponent();

                    // Оптимизация: в больших комнатах сразу отписываемся от видео новых участников
                    // Видео подключится автоматически, когда участник станет видимым через IntersectionObserver
                    const totalParticipants = (room?.remoteParticipants.size || 0) + 1;
                    if (totalParticipants > LARGE_ROOM_PARTICIPANTS_THRESHOLD) {
                        let unsubscribedCount = 0;
                        remoteParticipant.videoTrackPublications.forEach((publication) => {
                            if (
                                publication.source === Track.Source.Camera &&
                                publication instanceof RemoteTrackPublication &&
                                publication.isSubscribed
                            ) {
                                publication.setSubscribed(false);
                                unsubscribedCount++;
                            }
                        });

                        if (unsubscribedCount > 0) {
                            logger.debug(
                                `Отписались от видео нового участника ${remoteParticipant.identity} ` +
                                    `(всего: ${totalParticipants}, отписано: ${unsubscribedCount})`,
                            );
                        }
                    }
                }

                syncSnapshotCacheWithRoom();
            };

            const onParticipantDisconnected = (remoteParticipant: RemoteParticipant) => {
                const isAgent = checkIsAgent(remoteParticipant);

                if (!isAgent) {
                    raisedHandOrder.current = raisedHandOrder.current.filter((id) => id !== remoteParticipant.identity);
                    removeRemoteParticipant(remoteParticipant);
                    sortAfterDisconnect(remoteParticipant);
                    // Очищаем snapshot для покинувшего участника
                    clearParticipantSnapshot(remoteParticipant.identity);
                    syncSnapshotCacheWithRoom();
                }
            };

            // Debounce для предотвращения частых пересортировок при смене спикера
            let activeSpeakersDebounceTimer: ReturnType<typeof setTimeout> | null = null;
            const onActiveSpeakersChanged = (participants: Participant[]) => {
                updateParticipants(participants);

                if (activeSpeakersDebounceTimer) {
                    clearTimeout(activeSpeakersDebounceTimer);
                }

                activeSpeakersDebounceTimer = setTimeout(() => {
                    sortAfterSpeaking(participants);
                    activeSpeakersDebounceTimer = null;
                }, 300);
            };

            const onTrackSubscribed = (
                remoteTrack: RemoteTrack,
                remoteTrackPublication: RemoteTrackPublication,
                remoteParticipant: RemoteParticipant,
            ) => {
                setRemoteParticipant(remoteParticipant);

                if (remoteTrack?.source === Track.Source.ScreenShare) {
                    sortAfterScreenSharing(remoteParticipant);
                    addScreenShareParticipant(remoteParticipant);
                }

                if ((remoteTrack && remoteTrack.kind !== Track.Kind.Audio) || !room) {
                    return;
                }
                const tracks: AudioTrack[] = [];
                room.remoteParticipants.forEach((p) => {
                    p.audioTrackPublications.forEach((pub) => {
                        if (pub.audioTrack) {
                            tracks.push(pub.audioTrack);
                        }
                    });
                });

                isMounted() && setAudioTracks(tracks);
            };

            const onTrackUnsubscribed = (
                remoteTrack: RemoteTrack,
                remoteTrackPublication: RemoteTrackPublication,
                remoteParticipant: RemoteParticipant,
            ) => {
                setRemoteParticipant(remoteParticipant);

                if (remoteTrack.source === Track.Source.ScreenShare) {
                    removeScreenShareParticipant(remoteParticipant);
                }

                if ((remoteTrack && remoteTrack.kind !== Track.Kind.Audio) || !room) {
                    return;
                }

                isMounted() &&
                    setAudioTracks((prevAudioTracks) => {
                        return prevAudioTracks.filter((track) => track !== remoteTrack);
                    });
            };

            const onLocalTrackPublished = async (
                localTrackPublication: LocalTrackPublication,
                localParticipant: LocalParticipant,
            ) => {
                setLocalParticipant(localParticipant);

                if (localTrackPublication.source === Track.Source.ScreenShare) {
                    addScreenShareParticipant(localParticipant);
                    // Сразу понижаем камеру для освобождения ресурсов
                    setLocalCameraQuality(localParticipant, VideoQuality.LOW);
                    // Устанавливаем качество screen share синхронно
                    // setLocalScreenShareQuality проверяет наличие track и sender
                    // Если sender еще не готов, setPublishingQuality установится, а битрейт применится позже (не критично)
                    setLocalScreenShareQuality(localParticipant, VideoQuality.HIGH);
                }

                if (localTrackPublication.source === Track.Source.Camera) {
                    const localVideoTrack = localTrackPublication.track as LocalVideoTrack;
                    void setupRoomBackground(localVideoTrack).catch((error) => {
                        logger.error('[VirtualBackground] failed to apply room background', error);
                    });
                }
            };

            const onLocalTrackUnpublished = (
                localTrackPublication: LocalTrackPublication,
                localParticipant: LocalParticipant,
            ) => {
                setLocalParticipant(localParticipant);

                if (localTrackPublication.source === Track.Source.ScreenShare) {
                    removeScreenShareParticipant(localParticipant);
                }
            };

            const onAudioPlaybackStatusChanged = (args: unknown) => {
                console.warn('onAudioPlaybackStatusChanged', args);
            };

            const onConnectionStateChanged = (state: ConnectionState) => {
                if (state !== ConnectionState.Disconnected) {
                    setConnectionState(state);
                }
            };

            const onParticipantMetadataChanged = (prevMetadata: string | undefined, participant: Participant) => {
                playRaiseHandSound({
                    prevMetadata,
                    currMetadata: participant.metadata,
                });

                const isWasRaised = checkIsHandRaisedInMetadata(prevMetadata);
                const isNowRaised = checkIsHandRaisedInMetadata(participant.metadata);

                if (isNowRaised && !isWasRaised) {
                    raisedHandOrder.current = [
                        ...raisedHandOrder.current.filter((id) => id !== participant.identity),
                        participant.identity,
                    ];

                    const allParticipants = selectParticipants();
                    const orderedRaised = raisedHandOrder.current
                        .map((id) => allParticipants.find((p) => p.identity === id))
                        .filter(Boolean) as Participant[];
                    sortAfterHandRaised(orderedRaised);
                } else if (!isNowRaised && isWasRaised) {
                    raisedHandOrder.current = raisedHandOrder.current.filter((id) => id !== participant.identity);
                }

                if (participant.isLocal) {
                    setLocalParticipantProperty('metadata', participant.metadata);
                } else {
                    setRemoteParticipantProperty(participant.identity, 'metadata', participant.metadata);
                }
            };

            const onRoomMetadataChanged = (metadata: string | undefined) => {
                if (!hasRecorder) {
                    updateRoomMetadata(metadata);
                }
            };

            const onConnected = () => {
                setLocalParticipant(room.localParticipant);

                const remoteParticipants = excludeAgents<RemoteParticipant>(room.remoteParticipants);
                setRemoteParticipants(remoteParticipants);

                if (!hasRecorder) {
                    updateRoomMetadata(room.metadata);
                }

                const sortedParticipants = initialSort<Participant>([
                    room.localParticipant,
                    ...Array.from(remoteParticipants.values()),
                ]);
                setSortedParticipants(sortedParticipants);

                syncSnapshotCacheWithRoom();
                pingP2pOpponent();
            };

            room.once(RoomEvent.Disconnected, () => {
                room.off(RoomEvent.ParticipantConnected, onParticipantConnected)
                    .off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
                    .off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged)
                    .off(RoomEvent.TrackSubscribed, onTrackSubscribed)
                    .off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed)
                    .off(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
                    .off(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished)
                    .off(RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged)
                    .off(RoomEvent.ConnectionStateChanged, onConnectionStateChanged)
                    .off(RoomEvent.ParticipantMetadataChanged, onParticipantMetadataChanged)
                    .off(RoomEvent.RoomMetadataChanged, onRoomMetadataChanged)
                    .off(RoomEvent.Connected, onConnected);

                stopPingP2pOpponent();

                // Очищаем reactionsStore при отключении от комнаты
                reactionsStore.cleanup();
            });

            room.on(RoomEvent.ParticipantConnected, onParticipantConnected)
                .on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
                .on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged)
                .on(RoomEvent.TrackSubscribed, onTrackSubscribed)
                .on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed)
                .on(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
                .on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished)
                // trigger a state change by re-sorting participants
                .on(RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged)
                .on(RoomEvent.ConnectionStateChanged, onConnectionStateChanged)
                .on(RoomEvent.ParticipantMetadataChanged, onParticipantMetadataChanged)
                .on(RoomEvent.RoomMetadataChanged, onRoomMetadataChanged)
                .on(RoomEvent.Connected, onConnected);

            await room?.connect(url, token, options);
            setIsConnecting(false);
            setError(undefined);
            return room;
        } catch (error) {
            // Notify other participants that user is disconnected if there is an error
            room?.disconnect();

            setIsConnecting(false);
            if (error instanceof Error) {
                setError(error);
            } else {
                setError(new Error('an error has occured'));
            }

            return undefined;
        }
    }, []);

    return {
        connect: connectFn,
        isConnecting,
        room: room,
        error,
        audioTracks,
        connectionState,
    };
}
